import React, { useId, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { SessionUser } from 'models/auth/SessionUser';
import { clearAccessToken } from 'models/auth/accessTokenStore';

export type NavItem = {
  label: string;
  to?: string;
  children?: NavItem[];
};

type Props = {
  user: SessionUser;
  homeHref: string;
  navItems: NavItem[];
  children: React.ReactNode;
};

const AppShell: React.FC<Props> = ({ user, homeHref, navItems, children }) => {
  const navigate = useNavigate();
  const submenuBaseId = useId();
  const [expandedGroupLabel, setExpandedGroupLabel] = useState<string | null>(null);

  const expandedGroup = useMemo(() => {
    if (!expandedGroupLabel) return null;
    return navItems.find((item) => item.label === expandedGroupLabel && item.children?.length);
  }, [expandedGroupLabel, navItems]);

  const handleLogout = () => {
    clearAccessToken();
    localStorage.removeItem('userId');
    navigate('/login', { replace: true });
  };

  const toggleGroup = (label: string) => {
    setExpandedGroupLabel((current) => (current === label ? null : label));
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded focus:bg-white focus:px-3 focus:py-2 focus:shadow"
      >
        Skip to content
      </a>

      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <Link to={homeHref} className="text-lg font-semibold tracking-tight">
              Ink Voyd
            </Link>

            <nav className="hidden sm:flex items-center gap-3" aria-label="Primary">
              {navItems.map((item) => {
                const hasChildren = Boolean(item.children?.length);
                const isExpanded = hasChildren && expandedGroupLabel === item.label;
                const submenuId = `${submenuBaseId}-${item.label.replace(/\s+/g, '-').toLowerCase()}-submenu`;

                if (hasChildren) {
                  return (
                    <button
                      key={`group-${item.label}`}
                      type="button"
                      onClick={() => toggleGroup(item.label)}
                      aria-expanded={isExpanded}
                      aria-controls={submenuId}
                      className="text-sm text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1.5 py-0.5"
                    >
                      {item.label}
                    </button>
                  );
                }

                if (!item.to) return null;

                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="text-sm text-gray-700 hover:text-gray-900"
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/profile"
              className="text-sm text-gray-700 hover:text-gray-900 px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              My Dashboard
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              className="text-sm px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50"
            >
              Logout
            </button>
          </div>
        </div>

        {expandedGroup?.children?.length ? (
          <div className="hidden sm:block border-t border-gray-100">
            <nav
              id={`${submenuBaseId}-${expandedGroup.label.replace(/\s+/g, '-').toLowerCase()}-submenu`}
              aria-label={expandedGroup.label}
              className="mx-auto max-w-6xl px-4 py-2 flex items-center gap-3"
            >
              {expandedGroup.children
                .filter((child) => Boolean(child.to))
                .map((child) => (
                  <Link
                    key={child.to}
                    to={child.to!}
                    className="text-sm text-gray-700 hover:text-gray-900"
                    onClick={() => setExpandedGroupLabel(null)}
                  >
                    {child.label}
                  </Link>
                ))}
            </nav>
          </div>
        ) : null}
      </header>

      <main id="main-content" className="mx-auto max-w-6xl px-4 py-6">
        {children}
      </main>

      <footer className="border-t border-gray-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-4 text-sm text-gray-600 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
          <div className="flex gap-4">
            <Link className="hover:text-gray-900" to={homeHref}>Terms</Link>
            <Link className="hover:text-gray-900" to={homeHref}>Privacy</Link>
            <Link className="hover:text-gray-900" to={homeHref}>Help</Link>
          </div>
          <div className="text-xs text-gray-500">v0.1.0</div>
        </div>
      </footer>
    </div>
  );
};

export default AppShell;
